import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import {
  AlertError,
  Button,
  Form,
  FormFieldset,
  FormikInputText,
  FormikDropdownSearch,
  FormikPhoneInput,
  isFormikValid,
  FormLabel,
  FieldError,
} from '@percihealth/react';
import {
  ReferralCreateAvivaSchema,
  ReferralDocumentType,
  ReferralStoredDocument,
  ReferralStoredDocuments,
  MAX_REFERRAL_DOCUMENTS,
  filterReferralDocumentsOfType,
  ReferralCreateAviva,
  allJoinReasons,
  ReferralType,
} from '@packages/core-shared';
import { zodValidate } from '@packages/web-shared';
import {
  ReferralsApiRepository,
  useGenerateReferralId,
} from '@packages/web-shared/api';

import {
  DocumentUpload,
  ReferralInternalNotesField,
} from '@packages/web-shared/components/referrals';
import TrashIcon from '@packages/web-shared/components/icons/TrashIcon';

const joinReasonOptions = allJoinReasons.map((cr) => ({
  value: cr.id,
  name: cr.value,
}));

export default function ReferralForm() {
  const referralsApiRepository = useMemo(
    () => new ReferralsApiRepository(),
    [],
  );

  const navigate = useNavigate();
  const [responseError, setResponseError] = useState<string | undefined>(
    undefined,
  );

  const { referralId, errorMessage: referralIdError } = useGenerateReferralId();

  const referralHandleOnsubmit = async (values: any, actions: any) => {
    if (!isFormikValid(formik)) {
      return;
    }

    try {
      await referralsApiRepository.create({
        ...values,
        type: ReferralType.aviva,
      });

      navigate('/referred');
    } catch (error) {
      console.error(error);
      setResponseError((error as Error).message);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: referralId,
      patient: {
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        policyNumber: '',
      },
      joinReasonId: allJoinReasons[0].id,
      caseManager: {
        email: '',
        firstname: '',
        lastname: '',
      },
      employer: {
        contact: {
          firstname: '',
          lastname: '',
          email: '',
        },
      },
      notes: {
        internal: '',
      },
      documents: [],
    } as ReferralCreateAviva,
    validate: zodValidate(ReferralCreateAvivaSchema),
    onSubmit: referralHandleOnsubmit,
  });

  useEffect(() => {
    if (referralId) {
      void formik.setFieldValue('id', referralId, false);
    }
  }, [formik.setFieldValue, referralId]);

  // The documents are one typed array (PPL-3549): the sections below read
  // their own type out of it and write the whole array back
  const documents = formik.values.documents ?? [];
  const referralForms = filterReferralDocumentsOfType(
    documents,
    ReferralDocumentType.supporting,
  );

  const setDocuments = (updated: ReferralStoredDocuments) =>
    formik.setFieldValue('documents', updated, true /*validate*/);

  const addDocuments = (uploaded: ReferralStoredDocuments) =>
    setDocuments([...documents, ...uploaded]);

  const removeDocument = (document: ReferralStoredDocument) =>
    setDocuments(documents.filter((doc) => doc.path !== document.path));

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <FormFieldset title="Patient's details">
          <FormikInputText
            name="patient.firstname"
            required
            label="Patient first name"
            value={formik.values.patient.firstname}
          />
          <FormikInputText
            name="patient.lastname"
            required
            label="Patient surname"
            value={formik.values.patient.lastname}
          />
          <FormikInputText
            name="patient.email"
            required
            label="Patient email address"
            value={formik.values.patient.email}
            onBlur={(e) => {
              formik.setFieldValue(
                'patient.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'patient.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
          <FormikPhoneInput
            name="patient.phone"
            label="Patient telephone number"
            value={formik.values.patient.phone}
          />
          <FormikDropdownSearch
            label="Join reason"
            name="joinReasonId"
            required
            multiple={false}
            value={formik.values.joinReasonId.toString()}
            options={joinReasonOptions}
          />
        </FormFieldset>
        <FormFieldset title="Case manager">
          <FormikInputText
            id="caseManager.firstname"
            name="caseManager.firstname"
            required
            label="Case manager first name"
            value={formik.values.caseManager.firstname}
          />
          <FormikInputText
            id="caseManager.lastname"
            name="caseManager.lastname"
            required
            label="Case manager surname"
            value={formik.values.caseManager.lastname}
          />
          <FormikInputText
            id="caseManager.email"
            name="caseManager.email"
            required
            label="Case manager email address"
            value={formik.values.caseManager.email}
            onBlur={(e) => {
              formik.setFieldValue(
                'caseManager.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'caseManager.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
        </FormFieldset>
        <FormFieldset title="Employer details">
          <FormikInputText
            id="employer.contact.firstname"
            name="employer.contact.firstname"
            label="Employer first name (optional)"
            value={formik.values.employer?.contact?.firstname ?? ''}
          />
          <FormikInputText
            id="employer.contact.lastname"
            name="employer.contact.lastname"
            label="Employer surname (optional)"
            value={formik.values.employer?.contact?.lastname ?? ''}
          />
          <FormikInputText
            id="employer.contact.email"
            name="employer.contact.email"
            label="Employer email address (optional)"
            value={formik.values.employer?.contact?.email ?? ''}
            onBlur={(e) => {
              formik.setFieldValue(
                'employer.contact.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'employer.contact.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
        </FormFieldset>
        <FormFieldset title="Policy">
          <FormikInputText
            id="patient.policyNumber"
            name="patient.policyNumber"
            required
            placeholder="Aviva policy number"
            label="Policy number"
            value={formik.values.patient.policyNumber ?? ''}
          />
          <ReferralInternalNotesField />
        </FormFieldset>
        <FormFieldset title="Documents">
          <div>
            <FormLabel required>
              Referral forms (PDF, Microsoft Word, Images)
            </FormLabel>
            {referralForms.length > 0 && (
              <div style={{ marginLeft: '8px', marginBottom: '24px' }}>
                {referralForms.map((form) => (
                  <div
                    style={{ marginTop: '8px', display: 'flex', gap: '16px' }}
                    key={form.path}
                  >
                    <div style={{ wordBreak: 'break-word' }}>{form.name}</div>
                    <div
                      style={{
                        cursor: 'pointer',
                        color: 'var(--global-color-error)',
                      }}
                      onClick={() => removeDocument(form)}
                    >
                      <TrashIcon />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <DocumentUpload
              type={ReferralDocumentType.supporting}
              extensions=".pdf, .doc, .docx, .jpg, .jpeg, .png"
              referralId={referralId}
              slotsLeft={MAX_REFERRAL_DOCUMENTS - documents.length}
              onUploaded={addDocuments}
            />

            {typeof formik.errors.documents === 'string' &&
              formik.submitCount > 0 && (
                <FieldError style={{ position: 'initial' }}>
                  {formik.errors.documents}
                </FieldError>
              )}
          </div>
        </FormFieldset>

        <FormFieldset title="">
          <div>
            <Button
              type="submit"
              level="cta"
              size="large"
              disabled={!referralId}
              submitting={formik.isSubmitting}
            >
              Submit referral
            </Button>
          </div>
          <div className="full-width">
            {(responseError || referralIdError) && (
              <AlertError>{responseError || referralIdError}</AlertError>
            )}
          </div>
        </FormFieldset>
      </Form>
    </FormikProvider>
  );
}
